import { Injectable } from '@angular/core'
import { Rental, selectOnce, selectRentalSubUnits } from '@tokeet-frontend/tv3-platform'
import {
  updateRental,
  updateRentalDetails,
  updateRentalInstructions,
  updateRentalCustomFields,
  updateRentalGPS,
  updateRentalTaxes,
  updateRentalPaymentSchedule,
  updateRentalBaseRate,
  restrictRentalUsers,
  copyRentalBookingFees,
  copyRentalImages,
  getRentalBasicInfoPayload,
  getRentalCustomInfoPayload,
  getRentalDetailsPayload,
  getRentalInstructionsPayload,
} from '@tokeet-frontend/tv3-platform'
import { tap } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { Store } from '@ngrx/store'
import * as lodash from 'lodash'
import { SyncMainUnitData } from './sync-rental-data'

@Injectable({
  providedIn: 'root',
})
export class SyncMainUnitDataHelperService {
  constructor(private store: Store<any>) {}

  saveBasic(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(
        updateRental({
          payload: lodash.omit(getRentalBasicInfoPayload(mainRental), ['name', 'email', 'nickname']),
          rentalId: rental.id,
        })
      )
    })
  }

  saveDetails(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(
        updateRentalDetails({
          payload: getRentalDetailsPayload(mainRental),
          rentalId: rental.id,
        })
      )
    })
  }

  saveInstructions(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(
        updateRentalInstructions({
          payload: getRentalInstructionsPayload(mainRental),
          rentalId: rental.id,
        })
      )
    })
  }

  saveCustoms(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(
        updateRentalCustomFields({
          payload: getRentalCustomInfoPayload(mainRental),
          rentalId: rental.id,
        })
      )
    })
  }

  saveGPSLocation(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(updateRentalGPS({ gps: mainRental.gps, rentalId: rental.id }))
    })
  }

  saveTaxes(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(updateRentalTaxes({ taxes: mainRental.taxes, rentalId: rental.id }))
    })
  }

  savePaymentSchedules(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(updateRentalPaymentSchedule({ payload: mainRental.payment_schedule, rentalId: rental.id }))
    })
  }

  saveRateSettings(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      this.store.dispatch(
        updateRentalBaseRate({
          payload: {
            baserate: mainRental.baseRate,
            attributes: mainRental.attributes,
          },
          rentalId: rental.id,
        })
      )
    })
  }

  saveRestrictions(rentals: Rental[], mainRental: Rental) {
    lodash.map(rentals, (rental) => {
      const restrictedUserIds = mainRental.restrictedUsers || []
      const addedRestrictions = lodash.difference(restrictedUserIds, rental.restrictedUsers || [])
      const removedRestrictions = lodash.difference(rental.restrictedUsers || [], restrictedUserIds)

      this.store.dispatch(
        restrictRentalUsers({
          rental,
          addUserIds: addedRestrictions,
          removeUserIds: removedRestrictions,
          currentUserIds: restrictedUserIds || [],
        })
      )
    })
  }

  saveFees(rentals: Rental[], mainRental: Rental) {
    this.store.dispatch(
      copyRentalBookingFees({
        srcRentalId: mainRental.id,
        dstRentalIds: rentals.map((t) => t.id),
        resetDstRentals: true,
      })
    )
  }

  saveImages(rentals: Rental[], mainRental: Rental) {
    this.store.dispatch(
      copyRentalImages({
        srcRentalId: mainRental.id,
        dstRentalIds: rentals.map((t) => t.id),
        resetDstRentals: true,
      })
    )
  }

  saveToAllSubunits(mainRental: Rental, data: SyncMainUnitData) {
    return this.store
      .pipe(selectOnce(selectRentalSubUnits(mainRental.id)))
      .pipe(tap((rentals) => this.save(rentals, mainRental, data)))
  }

  save(rentals: Rental[], mainRental: Rental, data: SyncMainUnitData) {
    switch (data) {
      case SyncMainUnitData.Basic:
        this.saveBasic(rentals, mainRental)
        break

      case SyncMainUnitData.Details:
        this.saveDetails(rentals, mainRental)
        break

      case SyncMainUnitData.Gallery:
        this.saveImages(rentals, mainRental)
        break

      case SyncMainUnitData.Instructions:
        this.saveInstructions(rentals, mainRental)
        break

      case SyncMainUnitData.CustomInfo:
        this.saveCustoms(rentals, mainRental)
        break

      case SyncMainUnitData.Location:
        this.saveGPSLocation(rentals, mainRental)
        break

      case SyncMainUnitData.RateSettings:
        this.saveRateSettings(rentals, mainRental)
        break

      case SyncMainUnitData.Taxes:
        this.saveTaxes(rentals, mainRental)
        break

      case SyncMainUnitData.Fees:
        this.saveFees(rentals, mainRental)
        break

      case SyncMainUnitData.PaymentSchedules:
        this.savePaymentSchedules(rentals, mainRental)
        break

      case SyncMainUnitData.UserRestrictions:
        this.saveRestrictions(rentals, mainRental)
        break

      default:
        break
    }
  }
}
